import * as T from 'three';
import { RAIDER_FIRE, enemyProjectilePosition, raiderAimPose, type EnemyProjectileSnapshot, type TargetSnapshot, type Vec3 } from '@shooting-range/shared';
import { batchStatic, box, cylinder, material } from './world.js';

export interface RaiderRifle { group: T.Group; muzzle: T.Group; flash: T.Group; glint: T.Mesh }

const luminous = (color: number, opacity = 1) => new T.MeshBasicMaterial({ color, transparent: opacity < 1, opacity, depthWrite: false, toneMapped: false, side: T.DoubleSide });

export function buildRaiderRifle(): RaiderRifle {
  const group = new T.Group(); group.name = 'raider-rifle'; group.rotation.order = 'YXZ';
  group.position.set(RAIDER_FIRE.rifleSide, RAIDER_FIRE.rifleHeight, -RAIDER_FIRE.rifleForward);
  const steel = material(0x7b8070, 0.7, 0.38), dark = material(0x202922, 0.4, 0.55), glove = material(0x9a9475);
  box(group, [0.16, 0.19, 0.42], [0, 0, -0.23], steel);
  box(group, [0.13, 0.14, 0.24], [0, -0.015, 0.09], dark);
  box(group, [0.13, 0.24, 0.16], [0, -0.15, -0.2], dark).rotation.x = -0.2;
  box(group, [0.15, 0.12, 0.28], [0, 0, -0.57], dark);
  const barrel = cylinder(group, 0.045, 0.36, [0, 0, -0.77], steel, 10); barrel.rotation.x = Math.PI / 2;
  box(group, [0.06, 0.07, 0.07], [0, 0.11, -0.67], steel);
  // Hands stay attached to the rifle through raising, tracking and recoil.
  box(group, [0.17, 0.14, 0.16], [0.04, -0.12, -0.06], glove);
  box(group, [0.17, 0.14, 0.18], [-0.03, -0.1, -0.53], glove);
  batchStatic(group);
  const muzzle = new T.Group(); muzzle.name = 'raider-muzzle'; muzzle.position.z = -RAIDER_FIRE.barrelLength; group.add(muzzle);
  const flash = new T.Group(); flash.name = 'raider-muzzle-blast'; flash.visible = false; muzzle.add(flash);
  const star = new T.Shape();
  for (let i = 0; i < 16; i++) {
    const angle = i / 16 * Math.PI * 2, radius = i % 2 ? 0.14 : i % 4 ? 0.38 : 0.58;
    if (i === 0) star.moveTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
    else star.lineTo(Math.cos(angle) * radius, Math.sin(angle) * radius);
  }
  star.closePath();
  flash.add(new T.Mesh(new T.ShapeGeometry(star), luminous(0xffb043)));
  flash.add(new T.Mesh(new T.SphereGeometry(0.19, 8, 6), luminous(0xfff3cf)));
  const flame = new T.Mesh(new T.ConeGeometry(0.24, 0.85, 7), luminous(0xffcf73));
  flame.rotation.x = -Math.PI / 2; flame.position.z = -0.32; flash.add(flame);
  flash.add(new T.Mesh(new T.SphereGeometry(0.62, 8, 6), luminous(0xff9e38, 0.12)));
  const glint = new T.Mesh(new T.SphereGeometry(0.1, 8, 6), luminous(0xffb64e));
  glint.name = 'raider-aim-glint'; glint.visible = false; muzzle.add(glint);
  return { group, muzzle, flash, glint };
}

export function poseRaider(group: T.Group, target: TargetSnapshot, now: number, viewer: Vec3, fired?: EnemyProjectileSnapshot): void {
  const rifle = group.userData.rifle as RaiderRifle;
  const elapsed = fired ? now - fired.firedAt : Infinity;
  const shooting = elapsed >= 0 && elapsed < 650;
  const aim = target.aim;
  const destination = shooting ? fired!.destination : aim?.destination ?? viewer;
  const pose = raiderAimPose(group.position, destination);
  const raised = shooting ? 1 : aim ? T.MathUtils.clamp((now - aim.startsAt) / 260, 0, 1) : 0;
  const recoil = shooting ? Math.exp(-elapsed / 120) : 0;
  group.rotation.set(0, pose.yaw, 0);
  rifle.group.rotation.set(pose.pitch - (1 - raised) * 0.75 + recoil * 0.13, pose.rifleYaw, 0);
  rifle.group.position.z = -RAIDER_FIRE.rifleForward + recoil * 0.09;
  rifle.glint.visible = !!aim && !shooting;
  rifle.glint.scale.setScalar(0.6 + raised * (0.7 + Math.sin(now * 0.035) * 0.25));
  rifle.flash.visible = shooting && elapsed < 150;
  rifle.flash.scale.setScalar(0.65 + recoil * 0.6);
  rifle.flash.rotation.z = elapsed * 0.05;
  const limbs = group.userData.limbs as T.Group[];
  limbs.forEach((limb, index) => {
    limb.rotation.x = index % 2 ? 0.5 + raised * 0.75 + recoil * 0.15 : 0;
    limb.rotation.z = index === 1 ? raised * 0.5 : index === 3 ? -raised * 0.16 : 0;
  });
}

export function buildEnemyProjectile(): T.Group {
  const group = new T.Group(); group.name = 'enemy-tracer';
  const core = new T.Mesh(new T.SphereGeometry(0.09, 8, 6), luminous(0xfff4d6)); group.add(core);
  group.add(new T.Mesh(new T.SphereGeometry(0.23, 8, 6), luminous(0xff9c3a, 0.3)));
  // Volume stays visible head-on; ordinary WebGL lines collapse to one pixel along the camera ray.
  const tail = new T.Mesh(new T.CylinderGeometry(0.06, 0.015, 2.8, 6), luminous(0xffbd65, 0.8));
  tail.rotation.x = -Math.PI / 2; tail.position.z = 1.4; group.add(tail);
  return group;
}

export function positionEnemyProjectile(group: T.Group, projectile: EnemyProjectileSnapshot, now: number): void {
  group.position.copy(enemyProjectilePosition(projectile, now));
  const direction = new T.Vector3().subVectors(projectile.destination, projectile.origin).normalize();
  group.quaternion.setFromUnitVectors(new T.Vector3(0, 0, -1), direction);
  group.visible = now >= projectile.firedAt && now <= projectile.expiresAt;
  // Grow the trail out of the barrel instead of putting it through the shooter's body.
  group.scale.z = Math.min(1, Math.max(0.01, (now - projectile.firedAt) * RAIDER_FIRE.speed / 2800));
}
